// Agents › Approvals (`/c/{slug}/agents/approvals`, `/agents/approvals/{id}`; spec 06 screen 9;
// M3-08; research rec 9, 23): the approver's queue and each proposal with its change laid out
// field by field. Tabs: "Waiting for me" (`?state=pending&approver=me`, the queue), "Pending"
// (every open proposal the caller may view), "All". A pending row has Approve and Reject inline
// (one tap, rec 23); the detail adds "Edit and approve": the edited proposal is what executes
// (`approved_payload`), and an edited approval shows proposed and approved side by side.
// Decisions are idempotent server-side; the decider is the holder of the approver seat (05).
// The approvals routes are M3-03's: until they are registered on an API, this says so.
// Phase 1 inc 2: a decided approval also shows what became of its change (`execution_*`): an
// "Applied" badge, or "Not applied" with the server's reason ("Approved · not applied: …" on the
// detail), so an approval whose change did not happen never reads as plain "Approved".
// AGT-UI-2: the buttons are the shared `ApprovalActions` (approval-actions.tsx), the same as in a
// chat card and an inbox row.
import { ApiError } from "@companyos/sdk";
import {
  Badge,
  Button,
  EmptyState,
  InfoTip,
  ListRow,
  PageHeader,
  SettingsSection,
  Table,
  TableBody,
  TableCaption,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
  ViewTabs,
} from "@companyos/ui";
import { useInfiniteQuery, useQuery } from "@tanstack/react-query";
import { Link } from "@tanstack/react-router";
import { GaugeIcon, HandIcon } from "lucide-react";
import { useState } from "react";
import type { Api } from "../../api/client";
import type { ApprovalRow } from "../../api/types";
import { keepFocus } from "../../focus/focus";
import { NotFoundPage } from "../../pages/status-pages";
import { type CompanyAccess, isManager } from "../company";
import { PlanCheckBadge } from "../de/plan-check-badge";
import { UnverifiedBadge } from "../de/unverified-badge";
import { formatDateTime } from "../format";
import { Facts, LoadError, Loading, LoadMore, Section } from "../screen-parts";
import { isPendingRoute, pageQuery, spacesQuery } from "../work/work-queries";
import { formatElapsed, NotLiveYet } from "./agent-parts";
import { approvalQuery, approvalsQuery, runQuery } from "./agent-queries";
import { ApprovalActions, DecisionOutcome, RejectDialog } from "./approval-actions";
import { BoardNoteChange, type BoardNoteChangePayload } from "./board-note-change";
import { EscalationCard, hasCard } from "./escalation-card";
import { KpiEditDialog, KpiReviewCard } from "./kpi-review";
import {
  MemoryRuleEditDialog,
  MemoryRulePreview,
  memoryRuleChangeOf,
} from "./memory-rule-approval";
import { OutboxReceiptSection } from "./outbox-receipt";
import { RevertPanel } from "./revert-panel";
import { useDeNames } from "./runs-screen";
import { TaskBatchReview } from "./task-batch-review";

export const APPROVAL_TABS = ["mine", "pending", "all"] as const;
export type ApprovalTab = (typeof APPROVAL_TABS)[number];
const TAB_LABELS: Record<ApprovalTab, string> = {
  mine: "Waiting for me",
  pending: "Pending",
  all: "All",
};

export const APPROVAL_KIND_LABELS: Record<ApprovalRow["kind"], string> = {
  task_change: "Task change",
  message: "Message",
  page: "Page edit",
  external_action: "External action",
  promotion: "Promotion",
  delegation: "Delegation",
  budget_overrun: "Budget raise",
  kpi_value: "KPI value review",
  // Parity P6a: the typed proposals (their cards land with P6a in wave 1)
  task_move: "Task move",
  task_delete: "Task delete",
  board_config: "Board settings",
  automation: "Automation",
  playbook_gate: "Playbook gate",
  view_revision: "View change",
  bulk: "Bulk change",
  // D15 (preview): an agent's lesson, held before other agents are briefed on it
  memory_lesson: "Memory lesson",
  // AGENT-PLUMBING: a paid service an agent reported above what it may book by itself
  agent_cost: "Paid service (reported by agent)",
  // CALENDAR-C1: an agent's (or a non-writer's) calendar change, held for the calendar's writers
  calendar_change: "Calendar change",
  // T1-BOARD-MCP: an agent's template application, planned by the server, applied by a manager
  template_apply: "Template",
  task_metric: "Task metric",
  // T6-BOARD-NOTE: a DE's board note with the rule changes it describes, held as one
  board_note: "Board note and rules",
  // B2-GATES: one review over N tasks (keep / edit / send back / drop per row), and a person's
  // gates proposed by the Chief of Staff
  task_batch: "Batch review",
  gate_change: "Your gates",
  // BIZ-PLAYBOOKS: a DE's new version of a doc; BIZ-CLIENT-HEALTH: a DE's survey to a client
  doc_update: "Doc update",
  client_survey: "Client survey",
  // RECALL-RULES: a standing rule someone (or a DE) proposed for every agent or one DE
  memory_rule: "Standing rule",
  // CORE-KEYS: an agent call the policy held for approval (approved: the agent may make it once)
  agent_action: "Agent action (held by policy)",
  // CORE-AUTONOMY: the weekly review found an agent ready for the next autonomy level
  autonomy_change: "Autonomy level",
};

const STATE_BADGES: Record<
  ApprovalRow["state"],
  [string, "warning" | "success" | "danger" | "info" | "neutral"]
> = {
  pending: ["Pending", "warning"],
  approved: ["Approved", "success"],
  edited: ["Approved with edits", "info"],
  rejected: ["Rejected", "danger"],
  expired: ["Expired", "neutral"],
};

export function ApprovalStateBadge({ state }: { state: ApprovalRow["state"] }) {
  const [label, tone] = STATE_BADGES[state];
  return (
    <Badge variant={tone} data-approval-state={state}>
      {label}
    </Badge>
  );
}

type ExecutionView = {
  /** The badge: "Applied", "Not applied", … */
  label: string;
  /** The sentence: "Approved · not applied: <reason>", "Applied", … */
  text: string;
  tone: "warning" | "success" | "danger" | "info" | "neutral";
};

/**
 * Phase 1 inc 2: what became of an approved payload, apart from the decision (the server's
 * `execution_*`). An approval whose change did not happen never reads as plain "Approved".
 * Null when there is nothing to apply (undecided, rejected, expired) or nothing recorded.
 */
export function executionOf(
  a: Pick<
    ApprovalRow,
    "state" | "execution_state" | "execution_failure" | "execution_code" | "execution_reason"
  >,
): ExecutionView | null {
  if (a.state !== "approved" && a.state !== "edited") return null;
  const reason = a.execution_reason ?? "no reason was recorded";
  switch (a.execution_state) {
    case "applied":
      // STEP1-OUTBOX: an external action with no connection was logged only
      if (a.execution_code === "dry_run")
        return { label: "Dry run", text: `Approved · ${reason}`, tone: "neutral" };
      return {
        label: "Applied",
        text:
          a.execution_code === "already_satisfied" && a.execution_reason
            ? `Applied · ${a.execution_reason}`
            : "Applied",
        tone: "success",
      };
    case "failed":
      return a.execution_failure === "transient"
        ? {
            label: "Not applied yet",
            text: `Approved · not applied yet: ${reason}`,
            tone: "warning",
          }
        : { label: "Not applied", text: `Approved · not applied: ${reason}`, tone: "danger" };
    case "pending":
      // STEP1-OUTBOX: an external action waiting in the outbox
      if (a.execution_code === "queued")
        return { label: "In outbox", text: `Approved · ${reason}`, tone: "neutral" };
      return a.execution_reason
        ? { label: "Waiting", text: `Approved · not applied yet: ${reason}`, tone: "neutral" }
        : { label: "Applying…", text: "Approved · applying…", tone: "neutral" };
    default:
      return null;
  }
}

export function ApprovalExecutionBadge({ approval }: { approval: ApprovalRow }) {
  const x = executionOf(approval);
  if (!x) return null;
  return (
    <Badge variant={x.tone} data-execution-state={approval.execution_state} title={x.text}>
      {x.label}
    </Badge>
  );
}

/** AGENT-REPORTING: the kind filter chips (`approvals.list?kind=`). */
export const APPROVAL_KIND_FILTERS = ["kpi_value"] as const;
export type ApprovalKindFilter = (typeof APPROVAL_KIND_FILTERS)[number];

export function ApprovalsScreen({
  api,
  company,
  tab,
  onTab,
  kind,
  onKind,
}: {
  api: Api;
  company: CompanyAccess;
  tab: ApprovalTab;
  onTab: (tab: ApprovalTab) => void;
  /** Only this kind (the KPI reviews), or every kind. */
  kind?: ApprovalKindFilter | undefined;
  onKind?: (kind: ApprovalKindFilter | undefined) => void;
}) {
  const base =
    tab === "mine"
      ? ({ state: "pending", approver: "me" } as const)
      : tab === "pending"
        ? ({ state: "pending" } as const)
        : {};
  const filter = kind ? { ...base, kind } : base;
  const q = useInfiniteQuery(approvalsQuery(api, company.id, filter));
  const names = useDeNames(api, company.id);
  // AGT-UI-2: what was decided from this queue, newest first, with its live outcome: a decided
  // approval leaves "Waiting for me" / "Pending", and a change that fails after the decision is
  // still reported here.
  // Scoped to its company: a client-side switch to another company starts empty (the rows are
  // never seeded under the other company's query keys).
  const co = company.id;
  const [decidedIn, setDecided] = useState<{ co: string; list: ApprovalRow[] }>({ co, list: [] });
  const decided = decidedIn.co === co ? decidedIn.list : [];
  const onDecided = (d: ApprovalRow) =>
    setDecided((s) => ({
      co,
      list: [d, ...(s.co === co ? s.list : []).filter((x) => x.id !== d.id)],
    }));
  if (q.isSuccess && q.data.pages[0] === null)
    return <NotLiveYet icon={HandIcon} what="Approvals" build="the approvals build (M3-03)" />;
  const rows = (q.data?.pages ?? []).flatMap((p) => p?.data ?? []);
  const reviews = rows.filter((a) => a.kind === "kpi_value");
  const others = rows.filter((a) => a.kind !== "kpi_value");
  const nameOf = (a: ApprovalRow) =>
    a.requested_by_membership_id
      ? (names.get(a.requested_by_membership_id) ?? "A person")
      : "A person";
  return (
    <>
      <PageHeader
        spacing="tabs"
        eyebrow="Team"
        title="Approvals"
        badge={
          <InfoTip label="About Approvals">
            What your digital employees want to do that needs a person's yes: approve, edit or
            reject.
          </InfoTip>
        }
      />
      <ViewTabs
        aria-label="Approvals"
        value={tab}
        onValueChange={(v) => onTab(v as ApprovalTab)}
        tabs={APPROVAL_TABS.map((t) => ({ value: t, label: TAB_LABELS[t] }))}
      />
      <div className="flex min-w-0 flex-col gap-3 pt-5">
        {onKind ? (
          <fieldset className="flex flex-wrap items-center gap-1.5" data-slot="approval-kinds">
            <legend className="sr-only">Kind</legend>
            <Button
              size="sm"
              variant={kind ? "outline" : "secondary"}
              aria-pressed={!kind}
              onClick={() => onKind(undefined)}
            >
              All kinds
            </Button>
            <Button
              size="sm"
              variant={kind === "kpi_value" ? "secondary" : "outline"}
              aria-pressed={kind === "kpi_value"}
              onClick={() => onKind(kind === "kpi_value" ? undefined : "kpi_value")}
            >
              <GaugeIcon aria-hidden="true" />
              KPI reviews
            </Button>
          </fieldset>
        ) : null}
        {decided.length ? (
          <section
            aria-label="Decided just now"
            data-slot="approvals-decided"
            className="flex flex-col gap-0.5 rounded-lg border border-border px-3 py-2"
          >
            {decided.map((d) => (
              <DecisionOutcome key={d.id} api={api} co={company.id} approval={d} title />
            ))}
          </section>
        ) : null}
        {q.isPending ? (
          <Loading label="Loading approvals" rows={4} />
        ) : q.isError ? (
          <LoadError what="Approvals" error={q.error} />
        ) : !rows.length ? (
          <EmptyState
            variant="card"
            icon={HandIcon}
            title={
              kind === "kpi_value"
                ? "No KPI values wait for review"
                : tab === "mine"
                  ? "Nothing waits for you"
                  : "No approvals here"
            }
            description={
              kind === "kpi_value"
                ? "A digital employee's number on a metric flagged “Needs review” waits here until its seat's supervisor approves, edits or rejects it."
                : "A digital employee asks before it sends, posts or changes something consequential. Its proposals land here for the holder of the approver seat."
            }
          />
        ) : (
          <>
            {reviews.length ? (
              <div className="flex flex-col gap-3" data-slot="kpi-reviews">
                {reviews.map((a) => (
                  <KpiReviewCard
                    key={a.id}
                    api={api}
                    company={company}
                    approval={a}
                    requester={nameOf(a)}
                    reject={<RejectDialog api={api} co={company.id} approval={a} />}
                  />
                ))}
              </div>
            ) : null}
            {others.length ? (
              <div
                className="flex flex-col rounded-lg border border-border"
                data-slot="approvals-list"
              >
                {others.map((a) => (
                  <ApprovalListRow
                    key={a.id}
                    api={api}
                    company={company}
                    approval={a}
                    requester={nameOf(a)}
                    onDecided={onDecided}
                  />
                ))}
              </div>
            ) : null}
          </>
        )}
        <LoadMore
          what="approvals"
          hasMore={q.hasNextPage}
          loading={q.isFetchingNextPage}
          onMore={() => void q.fetchNextPage()}
        />
      </div>
    </>
  );
}

function ApprovalListRow({
  api,
  company,
  approval: a,
  requester,
  onDecided,
}: {
  api: Api;
  company: CompanyAccess;
  approval: ApprovalRow;
  requester: string;
  onDecided: (d: ApprovalRow) => void;
}) {
  return (
    <ListRow
      data-approval={a.id}
      className="border-b border-border last:border-b-0"
      title={a.proposal.title}
      render={
        <Link
          to="/c/$slug/agents/approvals/$id"
          params={{ slug: company.slug, id: a.id }}
          search={keepFocus}
        />
      }
      chips={
        <>
          <Badge variant="outline" className="max-sm:hidden">
            {APPROVAL_KIND_LABELS[a.kind]}
          </Badge>
          {/* DC-05: raised by an unverified DE (a pilot or an empty self-serve agent). */}
          {a.proposal.unverified ? <UnverifiedBadge /> : null}
          {/* PLAN-CHECK: the DE's memory check of its plan */}
          <PlanCheckBadge check={a.proposal.plan_check} />
          <ApprovalStateBadge state={a.state} />
          <ApprovalExecutionBadge approval={a} />
        </>
      }
      meta={`${requester} · ${formatElapsed(a.created_at)} ago`}
      trailing={
        // CORE-CARDS: a card is decided on its page, by one of its options
        a.state === "pending" && !hasCard(a) ? (
          // On a phone the row opens the detail, which has the same buttons. AGT-UI-2: the shared
          // ApprovalActions (the row leaves "Edit and approve" to the detail).
          <span className="relative z-10 max-sm:hidden">
            <ApprovalActions
              api={api}
              co={company.id}
              approval={a}
              size="sm"
              edit={false}
              onDecided={onDecided}
            />
          </span>
        ) : null
      }
    />
  );
}

/** One line per changed field: the value as text (objects as JSON). */
function show(v: unknown): string {
  if (v === null || v === undefined) return "—";
  if (typeof v === "string") return v;
  if (Array.isArray(v) && v.every((x) => typeof x === "string")) return v.join(", ");
  return JSON.stringify(v);
}

/** The proposal's change, field by field; with `approved`, proposed and approved side by side. */
function ChangeTable({
  proposed,
  approved,
}: {
  proposed: Record<string, unknown>;
  approved?: Record<string, unknown> | null;
}) {
  const fields = [...new Set([...Object.keys(proposed), ...Object.keys(approved ?? {})])];
  if (!fields.length) return <p className="text-sm text-muted-foreground">No fields.</p>;
  return (
    <Table data-slot="approval-change">
      <TableCaption>The change the proposal makes.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Field</TableHead>
          <TableHead scope="col">Proposed</TableHead>
          {approved ? <TableHead scope="col">Approved</TableHead> : null}
        </TableRow>
      </TableHeader>
      <TableBody>
        {fields.map((f) => {
          const changed = approved && show(approved[f]) !== show(proposed[f]);
          return (
            <TableRow key={f} data-changed={changed ? "true" : undefined}>
              <TableHead scope="row" className="font-mono text-xs">
                {f}
              </TableHead>
              <TableCell className="break-words whitespace-normal">{show(proposed[f])}</TableCell>
              {approved ? (
                <TableCell className="break-words whitespace-normal">
                  {show(approved[f])}
                  {changed ? (
                    <Badge variant="info" className="ml-2">
                      Edited
                    </Badge>
                  ) : null}
                </TableCell>
              ) : null}
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

type PlanLine = {
  kind: string;
  name: string;
  action: "create" | "reuse" | "link" | "skip";
  parent?: string | null;
  reason?: string | null;
};

const PLAN_ACTION: Record<PlanLine["action"], [string, "success" | "info" | "neutral"]> = {
  create: ["Create", "success"],
  reuse: ["Reuse", "info"],
  link: ["Link", "info"],
  skip: ["Skip", "neutral"],
};

/** T1-BOARD-MCP: a `template_apply` proposal's plan, one row per line, as the server made it. */
function TemplatePlan({ change }: { change: Record<string, unknown> }) {
  const seen = new Map<string, number>();
  const lines = ((Array.isArray(change.plan) ? change.plan : []) as PlanLine[]).map((l) => {
    const base = `${l.kind}:${l.name}:${l.parent ?? ""}`;
    const n = (seen.get(base) ?? 0) + 1;
    seen.set(base, n);
    return { ...l, key: `${base}:${n}` };
  });
  if (!lines.length) return <p className="text-sm text-muted-foreground">Nothing to apply.</p>;
  return (
    <Table data-slot="approval-plan">
      <TableCaption>Each line of the template.</TableCaption>
      <TableHeader>
        <TableRow>
          <TableHead scope="col">Action</TableHead>
          <TableHead scope="col">Kind</TableHead>
          <TableHead scope="col">Name</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {lines.map((l) => {
          const [label, variant] = PLAN_ACTION[l.action] ?? [l.action, "neutral"];
          return (
            <TableRow key={l.key}>
              <TableCell>
                <Badge variant={variant}>{label}</Badge>
              </TableCell>
              <TableCell className="text-muted-foreground">{l.kind.replace(/_/g, " ")}</TableCell>
              <TableCell className="break-words whitespace-normal">
                {l.name}
                {l.parent ? <span className="text-muted-foreground"> · {l.parent}</span> : null}
                {l.reason ? (
                  <span className="block text-xs text-muted-foreground">{l.reason}</span>
                ) : null}
              </TableCell>
            </TableRow>
          );
        })}
      </TableBody>
    </Table>
  );
}

const PRIORITY_NAMES = ["None", "Urgent", "High", "Normal", "Low"];

type ItemTemplateChange = {
  op: "item_template_create";
  name: string;
  description?: string | null;
  payload: {
    title: string;
    priority?: number;
    checklist?: string[];
    fields?: Record<string, unknown>;
    body?: unknown[];
    tag_ids?: string[];
    due_in_days?: number;
  };
};

/** B4-ITEM-TEMPLATES: a `board_config` proposal saving a board's item template, or null. */
function itemTemplateOf(kind: string, change: Record<string, unknown>): ItemTemplateChange | null {
  if (kind !== "board_config") return null;
  const c = change.change as { op?: string } | undefined;
  return c?.op === "item_template_create" ? (c as ItemTemplateChange) : null;
}

/** What a proposed item template fills a new task with. */
function ItemTemplatePreview({ t }: { t: ItemTemplateChange }) {
  const p = t.payload;
  const fields = Object.keys(p.fields ?? {}).length;
  return (
    <div data-slot="approval-item-template" className="flex flex-col gap-2 text-sm">
      <Facts
        items={[
          { label: "Name", value: t.name },
          ...(t.description ? [{ label: "When to use", value: t.description }] : []),
          { label: "Task title", value: p.title },
          ...(p.priority
            ? [{ label: "Priority", value: PRIORITY_NAMES[p.priority] ?? String(p.priority) }]
            : []),
          ...(p.body?.length ? [{ label: "Description", value: "Included" }] : []),
          ...(fields ? [{ label: "Fields", value: String(fields) }] : []),
          ...(p.tag_ids?.length ? [{ label: "Tags", value: String(p.tag_ids.length) }] : []),
          ...(p.due_in_days !== undefined
            ? [{ label: "Due", value: `${p.due_in_days} days after it is made` }]
            : []),
        ]}
      />
      {p.checklist?.length ? (
        <ul aria-label="Checklist" className="m-0 list-disc pl-5">
          {p.checklist.map((c, i) => (
            // biome-ignore lint/suspicious/noArrayIndexKey: items may repeat; the order is the key
            <li key={i}>{c}</li>
          ))}
        </ul>
      ) : null}
    </div>
  );
}

type DocUpdateChange = {
  page_id: string;
  title?: string;
  body_markdown: string;
  reason?: string;
};

/** BIZ-PLAYBOOKS: a DE's proposed new version of a doc: the doc, why, and the new body. */
function DocUpdatePreview({
  api,
  company,
  change,
}: {
  api: Api;
  company: CompanyAccess;
  change: DocUpdateChange;
}) {
  const page = useQuery({ ...pageQuery(api, company.id, change.page_id), retry: false });
  return (
    <div data-slot="approval-doc-update" className="flex flex-col gap-3 text-sm">
      <Facts
        items={[
          {
            label: "Doc",
            value: (
              <Link
                to="/c/$slug/work/p/$pageId"
                params={{ slug: company.slug, pageId: change.page_id }}
                search={keepFocus}
                className="text-primary-text hover:underline"
              >
                {page.data?.title ?? "Open the doc"}
              </Link>
            ),
          },
          ...(change.title && change.title !== page.data?.title
            ? [{ label: "New title", value: change.title }]
            : []),
          ...(change.reason ? [{ label: "Reason", value: change.reason }] : []),
        ]}
      />
      <pre
        data-slot="doc-update-body"
        className="m-0 max-h-96 overflow-y-auto rounded-md border border-subtle bg-sunken p-3 font-mono text-xs leading-5 break-words whitespace-pre-wrap [scrollbar-width:thin]"
      >
        {change.body_markdown}
      </pre>
    </div>
  );
}

type ClientSurveyChange = {
  space_id: string;
  kind: "nps" | "csat";
  recipient_name: string;
  recipient_email: string;
};

/** BIZ-CLIENT-HEALTH: a DE's NPS/CSAT survey to a client contact. */
function ClientSurveyPreview({
  api,
  company,
  change,
}: {
  api: Api;
  company: CompanyAccess;
  change: ClientSurveyChange;
}) {
  const spaces = useQuery(spacesQuery(api, company.id));
  const client = spaces.data?.data.find((s) => s.id === change.space_id);
  return (
    <div data-slot="approval-client-survey">
      <Facts
        items={[
          {
            label: "Client",
            value: client ? (
              <Link
                to="/c/$slug/work/s/$spaceId"
                params={{ slug: company.slug, spaceId: client.id }}
                search={
                  ((prev: Record<string, unknown>) => ({
                    ...keepFocus(prev),
                    view: "overview",
                  })) as never
                }
                className="text-primary-text hover:underline"
              >
                {client.name}
              </Link>
            ) : (
              "A client space"
            ),
          },
          { label: "Kind", value: change.kind === "nps" ? "NPS (0–10)" : "CSAT (1–5)" },
          { label: "To", value: change.recipient_name },
          { label: "Email", value: change.recipient_email },
        ]}
      />
    </div>
  );
}

export function ApprovalDetailScreen({
  api,
  company,
  id,
}: {
  api: Api;
  company: CompanyAccess;
  id: string;
}) {
  const q = useQuery(approvalQuery(api, company.id, id));
  const runId = q.data?.run_id ?? null;
  const run = useQuery({ ...runQuery(api, company.id, runId ?? "none"), enabled: runId !== null });
  const names = useDeNames(api, company.id);
  if (q.isError && isPendingRoute(q.error) && api.mode === "http")
    return <NotLiveYet icon={HandIcon} what="Approvals" build="the approvals build (M3-03)" />;
  if (q.isError && q.error instanceof ApiError && q.error.status === 404)
    return <NotFoundPage message="No such approval, or you may not view it." />;
  const a = q.data;
  const requester = a?.requested_by_membership_id
    ? (names.get(a.requested_by_membership_id) ?? "A person")
    : "A person";
  const decider = a?.decided_by_membership_id
    ? (names.get(a.decided_by_membership_id) ?? "The approver")
    : null;
  return (
    <>
      <PageHeader
        eyebrow={
          <Link
            to="/c/$slug/agents/approvals"
            params={{ slug: company.slug }}
            search={keepFocus}
            className="underline-offset-4 hover:underline"
          >
            Approvals
          </Link>
        }
        title={a ? a.proposal.title : "Approval"}
        badge={
          a ? (
            <span className="inline-flex flex-wrap items-center gap-1.5">
              {a.proposal.unverified ? <UnverifiedBadge /> : null}
              <PlanCheckBadge check={a.proposal.plan_check} />
              <ApprovalStateBadge state={a.state} />
              <ApprovalExecutionBadge approval={a} />
            </span>
          ) : null
        }
        subtitle={a ? `${APPROVAL_KIND_LABELS[a.kind]} · asked by ${requester}` : undefined}
        actions={
          a?.state === "pending" ? (
            <ApprovalActions
              api={api}
              co={company.id}
              approval={a}
              size="lg"
              // B2-GATES: a batch review applies from its rows; a DE's proposal may be sent back.
              // CORE-CARDS: a card is approved by one of its options (the Decision section)
              approveButton={a.kind !== "task_batch" && !hasCard(a)}
              edit={a.kind !== "task_batch" && a.kind !== "gate_change"}
              sendBack={a.actor_kind === "digital_employee"}
              editor={
                a.kind === "kpi_value" ? (
                  <KpiEditDialog api={api} company={company} approval={a} size="lg" />
                ) : a.kind === "memory_rule" ? (
                  <MemoryRuleEditDialog api={api} company={company} approval={a} size="lg" />
                ) : undefined
              }
            />
          ) : null
        }
      />
      {a ? <ExecutionNotice approval={a} /> : null}
      {a?.kind === "external_action" && (a.state === "approved" || a.state === "edited") ? (
        <OutboxReceiptSection api={api} companyId={company.id} approvalId={a.id} />
      ) : null}
      {/* CORE-REVERT: the run's sends and writes, with Undo and Revert (managers, switch on) */}
      {a?.run_id && isManager(company) ? (
        <RevertPanel api={api} company={company} target={{ kind: "run", id: a.run_id }} />
      ) : null}
      {q.isPending ? (
        <Loading label="Loading the approval" rows={3} />
      ) : q.isError ? (
        <LoadError what="The approval" error={q.error} />
      ) : a ? (
        <>
          {hasCard(a) ? (
            // CORE-CARDS: the escalation card; pending, its options decide it
            <Section id="approval-card" title="Decision">
              <EscalationCard api={api} co={company.id} approval={a} size="default" />
            </Section>
          ) : null}
          <SettingsSection title="Proposal" icon={HandIcon} data-slot="approval-facts">
            <Facts
              items={[
                { label: "Asked by", value: requester },
                ...(run.data
                  ? [
                      {
                        // ADR-0019: a run's proposal carries its verification verdict.
                        label: "Verification",
                        value:
                          run.data.verdict === "verified" ? (
                            <Badge variant="success">Verified</Badge>
                          ) : (
                            <Badge variant="warning" data-slot="unverified">
                              Unverified
                            </Badge>
                          ),
                      },
                    ]
                  : []),
                { label: "Asked", value: formatDateTime(a.created_at) },
                {
                  label: "Expires",
                  value: a.expires_at ? formatDateTime(a.expires_at) : "Does not expire",
                },
                ...(a.run_id
                  ? [
                      {
                        label: "From run",
                        value: (
                          <Link
                            to="/c/$slug/agents/runs/$id"
                            params={{ slug: company.slug, id: a.run_id }}
                            search={keepFocus}
                            className="text-primary-text underline-offset-4 hover:underline"
                          >
                            View run
                          </Link>
                        ),
                      },
                    ]
                  : []),
                ...(a.decided_at
                  ? [
                      {
                        label: "Decided",
                        value: `${formatDateTime(a.decided_at)}${decider ? ` by ${decider}` : ""}`,
                      },
                    ]
                  : []),
                ...(a.reason ? [{ label: "Reason", value: a.reason }] : []),
              ]}
            />
          </SettingsSection>
          {a.proposal.summary ? (
            <Section id="approval-why" title="Why">
              <p className="text-sm">{a.proposal.summary}</p>
            </Section>
          ) : null}
          {a.kind === "kpi_value" ? (
            <Section id="approval-kpi" title="Reported value">
              <KpiReviewCard
                api={api}
                company={company}
                approval={a}
                requester={requester}
                reject={null}
                decide={false}
              />
            </Section>
          ) : null}
          {a.kind === "template_apply" ? (
            <Section
              id="approval-plan"
              title="Plan"
              description="What applying the template creates, reuses or skips."
            >
              <TemplatePlan change={a.proposal.change} />
            </Section>
          ) : null}
          {itemTemplateOf(a.kind, a.proposal.change) ? (
            <Section id="approval-item-template" title="Task template">
              <ItemTemplatePreview
                t={itemTemplateOf(a.kind, a.proposal.change) as ItemTemplateChange}
              />
            </Section>
          ) : null}
          {a.kind === "board_note" ? (
            <Section id="approval-board-note" title="Note and rules">
              <BoardNoteChange
                api={api}
                co={company.id}
                change={
                  (a.state === "edited"
                    ? (a.approved_payload?.change ?? a.proposal.change)
                    : a.proposal.change) as unknown as BoardNoteChangePayload
                }
              />
            </Section>
          ) : null}
          {a.kind === "doc_update" ? (
            <Section id="approval-doc-update" title="New version">
              <DocUpdatePreview
                company={company}
                api={api}
                change={
                  (a.state === "edited"
                    ? (a.approved_payload?.change ?? a.proposal.change)
                    : a.proposal.change) as unknown as DocUpdateChange
                }
              />
            </Section>
          ) : a.kind === "client_survey" ? (
            <Section id="approval-client-survey" title="Survey">
              <ClientSurveyPreview
                company={company}
                api={api}
                change={a.proposal.change as unknown as ClientSurveyChange}
              />
            </Section>
          ) : a.kind === "memory_rule" ? (
            <Section id="approval-memory-rule" title="Rule">
              <MemoryRulePreview
                change={memoryRuleChangeOf(
                  a.state === "edited"
                    ? (a.approved_payload?.change ?? a.proposal.change)
                    : a.proposal.change,
                )}
              />
            </Section>
          ) : a.kind === "task_batch" ? (
            <Section id="approval-batch" title="Review">
              <TaskBatchReview api={api} co={company.id} approval={a} canDecide={a.can_decide} />
            </Section>
          ) : (
            <Section
              id="approval-change"
              title="Proposed change"
              description={
                a.state === "edited"
                  ? "Approved with edits: the approved column is what is applied."
                  : undefined
              }
            >
              <ChangeTable
                proposed={a.proposal.change}
                approved={a.state === "edited" ? (a.approved_payload?.change ?? null) : null}
              />
            </Section>
          )}
        </>
      ) : null}
    </>
  );
}

/** Phase 1 inc 2: the outcome as a sentence under the header ("Approved · not applied: …"). */
function ExecutionNotice({ approval: a }: { approval: ApprovalRow }) {
  const x = executionOf(a);
  if (!x) return null;
  const tone =
    x.tone === "danger"
      ? "border-destructive/40 text-destructive"
      : x.tone === "success"
        ? "border-border text-foreground"
        : "border-border text-muted-foreground";
  return (
    <p
      role="status"
      data-slot="approval-execution"
      data-execution-state={a.execution_state}
      data-execution-code={a.execution_code ?? undefined}
      className={`mb-4 rounded-lg border px-3 py-2 text-sm ${tone}`}
    >
      {x.text}
    </p>
  );
}
